import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI, razorpayAPI } from '../../lib/api';
import { 
  Check, 
  Sparkles,
  Building2,
  Zap,
  Crown,
  Users,
  FileText,
  Database,
  Shield,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

export default function PricingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [razorpayConfig, setRazorpayConfig] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        // Fetch plans without auth (public endpoint)
        const plansRes = await subscriptionAPI.getPlans();
        setPlans(plansRes.data);
        
        // Try to fetch Razorpay config (requires auth, so may fail)
        try {
          const configRes = await razorpayAPI.getConfig();
          setRazorpayConfig(configRes.data);
        } catch (configError) {
          // Config fetch failed (user not logged in), set default
          logger.debug('Razorpay config fetch skipped (not logged in)');
          setRazorpayConfig({ is_configured: false });
        }
      } catch (error) {
        logger.error('Failed to load plans:', error);
        toast.error('Failed to load pricing plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleSelectPlan = async (plan) => {
    setSelectedPlanId(plan.id);
    setSubscribing(true);

    try {
      // Check if user is logged in by making the API call
      const orderRes = await razorpayAPI.createOrder({
        plan_id: plan.id,
        billing_cycle: billingCycle
      });
      
      const orderData = orderRes.data;
      
      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_in_paise,
        currency: orderData.currency,
        name: 'HMC Buddy',
        description: `${orderData.plan_name} - ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            const verifyRes = await razorpayAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: plan.id,
              billing_cycle: billingCycle
            });
            
            toast.success(verifyRes.data.message || 'Subscription activated successfully!');
            navigate('/dashboard');
          } catch (error) {
            toast.error(apiErrorMessage(error, 'Payment verification failed'));
          }
        },
        prefill: orderData.prefill,
        notes: {
          institution_name: orderData.institution_name
        },
        theme: {
          color: '#EF4444' // red-500
        },
        modal: {
          ondismiss: function() {
            setSubscribing(false);
            setSelectedPlanId(null);
            toast.info('Payment cancelled');
          }
        }
      };
      
      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setSubscribing(false);
        setSelectedPlanId(null);
      });
      razorpay.open();
      
    } catch (error) {
      // If 401, user is not logged in - redirect to login/signup
      if (error.response?.status === 401) {
        toast.info('Please login or create an account to subscribe');
        navigate('/institution-signup');
      } else {
        toast.error(apiErrorMessage(error, 'Failed to initiate payment'));
      }
      setSubscribing(false);
      setSelectedPlanId(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'trial': return <Sparkles className="w-10 h-10" />;
      case 'starter': return <Building2 className="w-10 h-10" />;
      case 'pro': return <Zap className="w-10 h-10" />;
      case 'pro_plus': return <Crown className="w-10 h-10" />;
      default: return <Building2 className="w-10 h-10" />;
    }
  };

  const getPlanGradient = (planId) => {
    switch (planId) {
      case 'trial': return 'from-amber-500 to-orange-500';
      case 'starter': return 'from-blue-500 to-cyan-500';
      case 'pro': return 'from-red-500 to-rose-500';
      case 'pro_plus': return 'from-purple-500 to-pink-500';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const formatFeature = (feature) => {
    const featureMap = {
      'patient_management': 'Patient Management',
      'billing': 'Billing & Invoicing',
      'reports_basic': 'Basic Reports',
      'reports_advanced': 'Advanced Reports & Analytics',
      'pharmacy': 'Pharmacy Management',
      'lab': 'Laboratory Management',
      'appointments': 'Appointment Scheduling',
      'messaging': 'Internal Messaging',
      'imaging': 'Imaging/Radiology',
      'procedures': 'Procedures Tracking',
      'api_access': 'API Access',
      'priority_support': '24/7 Priority Support',
      'white_label': 'White Label Branding',
      'full_feature_access': 'Full Feature Access',
      'basic_analytics': 'Basic Analytics',
      'advanced_analytics': 'Advanced Analytics',
      'unlimited_users': 'Unlimited Staff Users',
      '300_patients_per_month': '300 Patients/Month',
      '1000_patients_per_month': '1000 Patients/Month',
      '3000_patients_per_month': '3000 Patients/Month'
    };
    return featureMap[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-red-50">
        <Loader2 className="w-12 h-12 animate-spin text-red-500" />
      </div>
    );
  }

  // Filter out trial plan for display, group the paid plans
  const paidPlans = plans.filter(p => p.id !== 'trial');
  const trialPlan = plans.find(p => p.id === 'trial');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      {/* Header */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            Choose Your Perfect Plan
          </h1>
          <p className="text-base sm:text-lg text-slate-600">
            Flexible pricing designed for clinics and hospitals of all sizes. Start with a free 15-day trial, no credit card required.
          </p>
        </div>

        {/* Trial Banner */}
        {trialPlan && (
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 shadow-lg">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-amber-500 rounded-xl text-white">
                      <Sparkles className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-2">
                        {trialPlan.name}
                      </h3>
                      <p className="text-slate-700 mb-3">{trialPlan.description}</p>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <div className="flex items-center gap-1 text-slate-600">
                          <FileText className="w-4 h-4" />
                          <span>100 Patient Registrations</span>
                        </div>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Database className="w-4 h-4" />
                          <span>All Features Included</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button 
                    size="lg"
                    className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg whitespace-nowrap"
                    onClick={() => navigate('/institution-signup')}
                  >
                    Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-white rounded-xl p-1.5 shadow-md border border-slate-200">
            <button
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-md' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-md' 
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly <span className="ml-1.5 text-emerald-600 font-semibold">Save 17%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid - All Plans Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {paidPlans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative border-2 shadow-xl hover:shadow-2xl transition-all ${
                plan.popular ? 'border-red-400 scale-105 z-10' : 'border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-red-500 to-rose-500 text-white text-sm font-semibold rounded-full shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                {/* Plan Icon & Name */}
                <div className="text-center mb-6">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${getPlanGradient(plan.id)} text-white mb-4 shadow-lg`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h2>
                  <p className="text-slate-600 text-sm">{plan.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-900">
                      {billingCycle === 'yearly' ? plan.price_yearly_formatted : plan.price_monthly_formatted}
                    </span>
                    <span className="text-slate-500">
                      /{billingCycle === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-emerald-600 font-medium text-sm mt-2">
                      Save ₹{plan.yearly_savings?.toLocaleString()}/year
                    </p>
                  )}
                </div>

                {/* Key Metrics */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">Staff Users</span>
                    </div>
                    <span className="font-bold text-slate-900">
                      {plan.max_users === -1 ? 'Unlimited' : plan.max_users}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-slate-600">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm">Patients/Month</span>
                    </div>
                    <span className="font-bold text-slate-900">
                      {plan.max_patients_per_month === -1 ? 'Unlimited' : plan.max_patients_per_month?.toLocaleString()}
                    </span>
                  </div>
                  <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Database className="w-4 h-4" />
                      <span className="text-sm font-medium">Data Retention</span>
                    </div>
                    <div className="space-y-1 pl-6">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-600">Standard:</span>
                        <span className="text-sm font-bold text-slate-900">3 years</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-600">Medico-Legal:</span>
                        <span className="text-sm font-bold text-slate-900">10 years</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="flex-1 mb-6">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wide">Features Included:</h3>
                  <div className="space-y-3">
                    {plan.features?.slice(0, 8).map((feature, idx) => (
                      <div key={feature} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-emerald-600" />
                        </div>
                        <span className="text-slate-700 text-sm leading-relaxed">
                          {formatFeature(feature)}
                        </span>
                      </div>
                    ))}
                    {plan.features && plan.features.length > 8 && (
                      <div className="text-xs text-slate-500 pl-7">
                        +{plan.features.length - 8} more features
                      </div>
                    )}
                  </div>
                </div>

                {/* CTA Button */}
                <Button 
                  size="lg"
                  className={`w-full ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600' 
                      : 'bg-slate-900 hover:bg-slate-800'
                  } text-white shadow-lg h-12`}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={subscribing && selectedPlanId === plan.id}
                >
                  {subscribing && selectedPlanId === plan.id ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Get Started <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Security Note */}
        <div className="max-w-4xl mx-auto mt-8">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-blue-900 mb-1">Secure Payment via Razorpay</p>
                  <p className="text-blue-700 text-sm">
                    Your data and payments are protected with industry-leading security. All transactions are encrypted and PCI-DSS compliant.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12 text-slate-600">
          <p className="text-sm">
            All plans include free updates and email support. No hidden fees.
          </p>
          <p className="text-sm mt-2">
            Already have an account? <button onClick={() => navigate('/login')} className="text-red-600 hover:text-red-700 font-medium underline">Login here</button>
          </p>
        </div>
      </div>
    </div>
  );
}
